import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Table,
  Button,
  Group,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Badge,
  Skeleton,
  Alert,
  Drawer,
  ThemeIcon,
  Checkbox,
  Tooltip,
  Select,
  PasswordInput,
  Switch,
  SimpleGrid,
  Divider,
  Progress,
  ScrollArea,
  MultiSelect,
  CopyButton,
} from '@mantine/core';
import { useDisclosure, useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconTrash,
  IconUsers,
  IconAlertTriangle,
  IconSearch,
  IconRefresh,
  IconUser,
  IconUserOff,
  IconUserCheck,
  IconLock,
  IconLockOpen,
  IconKey,
  IconMail,
  IconId,
  IconCalendar,
  IconShieldCheck,
  IconUsersGroup,
  IconUserPlus,
  IconUserMinus,
  IconCheck,
  IconCopy,
  IconFolderPlus,
} from '@tabler/icons-react';

import {
  useAdUsers,
  useAdUser,
  useAdGroups,
  useAdSummary,
  useCreateAdUser,
  useDeleteAdUser,
  useResetAdPassword,
  useEnableAdUser,
  useDisableAdUser,
  useUnlockAdUser,
  useAddUserToGroup,
  useRemoveUserFromGroup,
  useCreateAdGroup,
  useCreateAdOu,
  type AdUser,
  type AdUserFilters,
  type CreateAdUserInput,
  type CreateAdGroupInput,
  type CreateAdOuInput,
} from '@/hooks/useAdUsers';

// Password generator
function generatePassword(length = 12): string {
  const lowercase = 'abcdefghijkmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const numbers = '23456789';
  const special = '!@#$%&*';

  const allChars = lowercase + uppercase + numbers + special;
  let password = '';

  // Ensure at least one of each type
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  // Fill the rest
  for (let i = password.length; i < length; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  // Shuffle
  return password.split('').sort(() => Math.random() - 0.5).join('');
}

export function AdUsersPage() {
  // State
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [groupFilter, setGroupFilter] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [pageVisible, setPageVisible] = useState(false);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());

  // Modals
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure();
  const [passwordModalOpened, { open: openPasswordModal, close: closePasswordModal }] = useDisclosure();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure();
  const [bulkGroupModalOpened, { open: openBulkGroupModal, close: closeBulkGroupModal }] = useDisclosure();
  const [createGroupModalOpened, { open: openCreateGroupModal, close: closeCreateGroupModal }] = useDisclosure();
  const [createOuModalOpened, { open: openCreateOuModal, close: closeCreateOuModal }] = useDisclosure();

  // Selected items
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdUser | null>(null);
  const [passwordUser, setPasswordUser] = useState<AdUser | null>(null);

  // Filters
  const filters: AdUserFilters = {
    search: debouncedSearch || undefined,
    enabled: statusFilter === 'enabled' ? true : statusFilter === 'disabled' ? false : undefined,
    locked: statusFilter === 'locked' ? true : undefined,
    group: groupFilter || undefined,
  };

  // Queries
  const { data, isLoading, isError, refetch, isRefetching } = useAdUsers(filters);
  const { data: groupsData } = useAdGroups();
  const { data: summaryData } = useAdSummary();

  // Mutations
  const createUser = useCreateAdUser();
  const createGroup = useCreateAdGroup();
  const createOu = useCreateAdOu();
  const deleteUser = useDeleteAdUser();
  const resetPassword = useResetAdPassword();
  const enableUser = useEnableAdUser();
  const disableUser = useDisableAdUser();
  const unlockUser = useUnlockAdUser();

  // Animation effects
  useEffect(() => {
    setPageVisible(true);
  }, []);

  useEffect(() => {
    if (data?.users) {
      setVisibleRows(new Set());
      data.users.forEach((_, index) => {
        setTimeout(() => {
          setVisibleRows((prev) => new Set([...prev, index]));
        }, index * 20);
      });
    }
  }, [data?.users]);

  // Form state for create user
  const [createForm, setCreateForm] = useState<CreateAdUserInput>({
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    email: '',
    description: '',
    groups: [],
    mustChangePassword: true,
  });
  const [showPassword, setShowPassword] = useState(false);

  // Password form state
  const [newPassword, setNewPassword] = useState('');
  const [mustChangePassword, setMustChangePassword] = useState(true);

  // Create group form state
  const [createGroupForm, setCreateGroupForm] = useState<CreateAdGroupInput>({
    name: '',
    description: '',
  });

  // Create OU form state
  const [createOuForm, setCreateOuForm] = useState<CreateAdOuInput>({
    dn: '',
    description: '',
  });

  const resetCreateForm = () => {
    setCreateForm({
      username: '',
      firstName: '',
      lastName: '',
      password: '',
      email: '',
      description: '',
      groups: [],
      mustChangePassword: true,
    });
    setShowPassword(false);
  };

  const resetCreateGroupForm = () => {
    setCreateGroupForm({
      name: '',
      description: '',
    });
  };

  const resetCreateOuForm = () => {
    setCreateOuForm({
      dn: '',
      description: '',
    });
  };

  const handleGeneratePassword = () => {
    const password = generatePassword(12);
    setCreateForm({ ...createForm, password });
    setShowPassword(true);
  };

  const handleGenerateNewPassword = () => {
    const password = generatePassword(12);
    setNewPassword(password);
  };

  const handleCreateUser = async () => {
    if (!createForm.username.trim() || !createForm.firstName.trim() || !createForm.lastName.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Username, first name, and last name are required',
        color: 'red',
      });
      return;
    }

    if (!createForm.password || createForm.password.length < 8) {
      notifications.show({
        title: 'Validation Error',
        message: 'Password must be at least 8 characters',
        color: 'red',
      });
      return;
    }

    try {
      await createUser.mutateAsync(createForm);
      notifications.show({
        title: 'User Created',
        message: `User ${createForm.username} has been created`,
        color: 'green',
      });
      closeCreateModal();
      resetCreateForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create user',
        color: 'red',
      });
    }
  };

  const handleCreateGroup = async () => {
    if (!createGroupForm.name.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Group name is required',
        color: 'red',
      });
      return;
    }

    try {
      await createGroup.mutateAsync(createGroupForm);
      notifications.show({
        title: 'Group Created',
        message: `Group ${createGroupForm.name} has been created`,
        color: 'green',
      });
      closeCreateGroupModal();
      resetCreateGroupForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create group',
        color: 'red',
      });
    }
  };

  const handleCreateOu = async () => {
    if (!createOuForm.dn.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'OU DN is required',
        color: 'red',
      });
      return;
    }

    try {
      await createOu.mutateAsync(createOuForm);
      notifications.show({
        title: 'OU Created',
        message: `Organizational Unit has been created`,
        color: 'green',
      });
      closeCreateOuModal();
      resetCreateOuForm();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create OU',
        color: 'red',
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;

    try {
      await deleteUser.mutateAsync(deletingUser.username);
      notifications.show({
        title: 'User Deleted',
        message: `User ${deletingUser.username} has been deleted`,
        color: 'green',
      });
      closeDeleteModal();
      setDeletingUser(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to delete user',
        color: 'red',
      });
    }
  };

  const handleResetPassword = async () => {
    if (!passwordUser || !newPassword) return;

    if (newPassword.length < 8) {
      notifications.show({
        title: 'Validation Error',
        message: 'Password must be at least 8 characters',
        color: 'red',
      });
      return;
    }

    try {
      await resetPassword.mutateAsync({
        userId: passwordUser.username,
        newPassword,
        mustChangePassword,
      });
      notifications.show({
        title: 'Password Reset',
        message: `Password for ${passwordUser.username} has been reset`,
        color: 'green',
      });
      closePasswordModal();
      setPasswordUser(null);
      setNewPassword('');
      setMustChangePassword(true);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to reset password',
        color: 'red',
      });
    }
  };

  const handleToggleUserStatus = async (user: AdUser) => {
    try {
      if (user.enabled) {
        await disableUser.mutateAsync(user.username);
        notifications.show({
          title: 'User Disabled',
          message: `${user.username} has been disabled`,
          color: 'yellow',
        });
      } else {
        await enableUser.mutateAsync(user.username);
        notifications.show({
          title: 'User Enabled',
          message: `${user.username} has been enabled`,
          color: 'green',
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update user status',
        color: 'red',
      });
    }
  };

  const handleUnlockUser = async (user: AdUser) => {
    try {
      await unlockUser.mutateAsync(user.username);
      notifications.show({
        title: 'User Unlocked',
        message: `${user.username} has been unlocked`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to unlock user',
        color: 'red',
      });
    }
  };

  const handleOpenDrawer = (user: AdUser) => {
    setSelectedUserId(user.username);
    openDrawer();
  };

  const handleOpenDelete = (user: AdUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingUser(user);
    openDeleteModal();
  };

  const handleOpenPassword = (user: AdUser, e: React.MouseEvent) => {
    e.stopPropagation();
    setPasswordUser(user);
    setNewPassword('');
    setMustChangePassword(true);
    openPasswordModal();
  };

  const handleSelectAll = () => {
    if (!data?.users) return;
    if (selectedUsers.length === data.users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(data.users.map((u) => u.username));
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const users = data?.users || [];
  const groups = groupsData?.groups || [];
  const summary = summaryData || { totalUsers: 0, enabledUsers: 0, disabledUsers: 0, lockedUsers: 0, totalGroups: 0 };

  const groupOptions = groups
    .filter((g) => g && g.name)
    .map((g) => ({ value: g.name, label: g.name }));

  return (
    <Container
      size="xl"
      py="md"
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <Stack gap="lg">
        {/* Page Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Active Directory Users</Title>
            <Text c="dimmed" size="sm">
              Manage users and groups in Samba AD
            </Text>
          </div>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
              loading={isRefetching}
            >
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconFolderPlus size={16} />} onClick={openCreateOuModal}>
              Create OU
            </Button>
            <Button variant="light" leftSection={<IconUsersGroup size={16} />} onClick={openCreateGroupModal}>
              Create Group
            </Button>
            <Button leftSection={<IconPlus size={16} />} onClick={openCreateModal}>
              Create User
            </Button>
          </Group>
        </Group>

        {/* Summary Cards */}
        <SimpleGrid cols={{ base: 2, sm: 4, lg: 5 }}>
          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" size="lg" color="blue">
                <IconUsers size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Total Users
                </Text>
                <Text size="xl" fw={700}>
                  {summary.totalUsers}
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" size="lg" color="green">
                <IconUserCheck size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Enabled
                </Text>
                <Text size="xl" fw={700}>
                  {summary.enabledUsers}
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" size="lg" color="gray">
                <IconUserOff size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Disabled
                </Text>
                <Text size="xl" fw={700}>
                  {summary.disabledUsers}
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" size="lg" color="red">
                <IconLock size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Locked
                </Text>
                <Text size="xl" fw={700}>
                  {summary.lockedUsers}
                </Text>
              </div>
            </Group>
          </Paper>

          <Paper withBorder p="md" radius="md">
            <Group gap="xs">
              <ThemeIcon variant="light" size="lg" color="violet">
                <IconUsersGroup size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
                  Groups
                </Text>
                <Text size="xl" fw={700}>
                  {summary.totalGroups}
                </Text>
              </div>
            </Group>
          </Paper>
        </SimpleGrid>

        {/* Filters */}
        <Paper withBorder p="md" radius="md">
          <Group gap="md">
            <TextInput
              placeholder="Search users..."
              leftSection={<IconSearch size={16} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ flex: 1, maxWidth: 300 }}
            />
            <Select
              placeholder="Status"
              data={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
                { value: 'locked', label: 'Locked' },
              ]}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              style={{ width: 150 }}
            />
            <Select
              placeholder="Group"
              data={groupOptions}
              value={groupFilter}
              onChange={setGroupFilter}
              clearable
              searchable
              style={{ width: 200 }}
            />
            {(searchValue || statusFilter || groupFilter) && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setSearchValue('');
                  setStatusFilter(null);
                  setGroupFilter(null);
                }}
              >
                Clear Filters
              </Button>
            )}
          </Group>
        </Paper>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <Paper withBorder p="sm" radius="md" bg="blue.0">
            <Group justify="space-between">
              <Text size="sm" fw={500}>
                {selectedUsers.length} user{selectedUsers.length !== 1 ? 's' : ''} selected
              </Text>
              <Group gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconUsersGroup size={14} />}
                  onClick={openBulkGroupModal}
                >
                  Add to Group
                </Button>
                <Button
                  size="xs"
                  variant="light"
                  color="yellow"
                  leftSection={<IconUserOff size={14} />}
                  onClick={() => {
                    // TODO: Bulk disable
                  }}
                >
                  Disable
                </Button>
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setSelectedUsers([])}
                >
                  Clear Selection
                </Button>
              </Group>
            </Group>
          </Paper>
        )}

        {/* Users Table */}
        <Paper withBorder radius="md">
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} height={50} />
              ))}
            </Stack>
          ) : isError ? (
            <Alert color="red" m="md" icon={<IconAlertTriangle size={16} />}>
              Failed to load users. Make sure the AD connection is configured.
            </Alert>
          ) : users.length === 0 ? (
            <Stack align="center" p="xl" gap="md">
              <ThemeIcon variant="light" size={60} color="gray">
                <IconUsers size={30} />
              </ThemeIcon>
              <Text c="dimmed">No users found</Text>
              {(searchValue || statusFilter || groupFilter) ? (
                <Text size="sm" c="dimmed">
                  Try adjusting your filters
                </Text>
              ) : (
                <Button variant="light" onClick={openCreateModal}>
                  Create your first user
                </Button>
              )}
            </Stack>
          ) : (
            <ScrollArea>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={40}>
                      <Checkbox
                        checked={selectedUsers.length === users.length && users.length > 0}
                        indeterminate={selectedUsers.length > 0 && selectedUsers.length < users.length}
                        onChange={handleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th>User</Table.Th>
                    <Table.Th>Username</Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Groups</Table.Th>
                    <Table.Th>Last Logon</Table.Th>
                    <Table.Th w={120}>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {users.map((user, index) => (
                    <Table.Tr
                      key={user.username}
                      style={{
                        cursor: 'pointer',
                        opacity: visibleRows.has(index) ? 1 : 0,
                        transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                      }}
                      onClick={() => handleOpenDrawer(user)}
                    >
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedUsers.includes(user.username)}
                          onChange={() => handleSelectUser(user.username)}
                        />
                      </Table.Td>
                      <Table.Td>
                        <Group gap="sm">
                          <ThemeIcon
                            variant="light"
                            size="md"
                            color={user.enabled ? 'blue' : 'gray'}
                          >
                            <IconUser size={16} />
                          </ThemeIcon>
                          <div>
                            <Text size="sm" fw={500}>
                              {user.displayName || `${user.firstName} ${user.lastName}`}
                            </Text>
                            {user.description && (
                              <Text size="xs" c="dimmed" lineClamp={1}>
                                {user.description}
                              </Text>
                            )}
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {user.username}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={user.email ? undefined : 'dimmed'}>
                          {user.email || '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4}>
                          {user.locked && (
                            <Tooltip label="Account locked">
                              <Badge color="red" variant="light" size="sm" leftSection={<IconLock size={10} />}>
                                Locked
                              </Badge>
                            </Tooltip>
                          )}
                          {!user.enabled && (
                            <Badge color="gray" variant="light" size="sm">
                              Disabled
                            </Badge>
                          )}
                          {user.enabled && !user.locked && (
                            <Badge color="green" variant="light" size="sm">
                              Active
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {(user.groups?.length ?? 0) > 0 ? (
                          <Group gap={4}>
                            <Badge variant="outline" size="sm">
                              {user.groups![0]}
                            </Badge>
                            {user.groups!.length > 1 && (
                              <Tooltip label={user.groups!.slice(1).join(', ')}>
                                <Badge variant="light" size="sm" color="gray">
                                  +{user.groups!.length - 1}
                                </Badge>
                              </Tooltip>
                            )}
                          </Group>
                        ) : (
                          <Text size="sm" c="dimmed">-</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" c="dimmed">
                          {user.lastLogon ? formatDate(user.lastLogon) : 'Never'}
                        </Text>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap={4}>
                          <Tooltip label="Reset password">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="blue"
                              onClick={(e) => handleOpenPassword(user, e)}
                            >
                              <IconKey size={16} />
                            </ActionIcon>
                          </Tooltip>
                          {user.locked ? (
                            <Tooltip label="Unlock user">
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color="green"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleUnlockUser(user);
                                }}
                                loading={unlockUser.isPending}
                              >
                                <IconLockOpen size={16} />
                              </ActionIcon>
                            </Tooltip>
                          ) : (
                            <Tooltip label={user.enabled ? 'Disable user' : 'Enable user'}>
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                color={user.enabled ? 'yellow' : 'green'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleUserStatus(user);
                                }}
                                loading={enableUser.isPending || disableUser.isPending}
                              >
                                {user.enabled ? <IconUserOff size={16} /> : <IconUserCheck size={16} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                          <Tooltip label="Delete user">
                            <ActionIcon
                              variant="subtle"
                              size="sm"
                              color="red"
                              onClick={(e) => handleOpenDelete(user, e)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          )}
        </Paper>
      </Stack>

      {/* Create User Modal */}
      <Modal
        opened={createModalOpened}
        onClose={() => {
          closeCreateModal();
          resetCreateForm();
        }}
        title="Create New User"
        size="lg"
      >
        <Stack gap="md">
          <SimpleGrid cols={2}>
            <TextInput
              label="First Name"
              placeholder="John"
              required
              value={createForm.firstName}
              onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              required
              value={createForm.lastName}
              onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
            />
          </SimpleGrid>

          <TextInput
            label="Username"
            placeholder="jdoe"
            description="Login name for the user"
            required
            leftSection={<IconUser size={16} />}
            value={createForm.username}
            onChange={(e) => setCreateForm({ ...createForm, username: e.target.value.toLowerCase().replace(/\s/g, '') })}
          />

          <TextInput
            label="Email"
            placeholder="john.doe@example.com"
            leftSection={<IconMail size={16} />}
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />

          <Textarea
            label="Description"
            placeholder="Optional description..."
            minRows={2}
            value={createForm.description}
            onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
          />

          <Divider label="Password" labelPosition="center" />

          <Group align="flex-end">
            <PasswordInput
              label="Password"
              placeholder="Enter password"
              required
              style={{ flex: 1 }}
              visible={showPassword}
              onVisibilityChange={setShowPassword}
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
            />
            <Button variant="light" onClick={handleGeneratePassword}>
              Generate
            </Button>
            {createForm.password && (
              <CopyButton value={createForm.password}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied!' : 'Copy password'}>
                    <ActionIcon variant="light" color={copied ? 'green' : 'gray'} onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
          </Group>

          <Switch
            label="User must change password at next logon"
            checked={createForm.mustChangePassword}
            onChange={(e) => setCreateForm({ ...createForm, mustChangePassword: e.currentTarget.checked })}
          />

          <Divider label="Groups" labelPosition="center" />

          <MultiSelect
            label="Add to Groups"
            placeholder="Select groups..."
            data={groupOptions}
            value={createForm.groups}
            onChange={(value) => setCreateForm({ ...createForm, groups: value })}
            searchable
            clearable
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeCreateModal(); resetCreateForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              loading={createUser.isPending}
              leftSection={<IconPlus size={16} />}
            >
              Create User
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        opened={passwordModalOpened}
        onClose={() => {
          closePasswordModal();
          setPasswordUser(null);
          setNewPassword('');
        }}
        title="Reset Password"
        size="md"
      >
        <Stack gap="md">
          {passwordUser && (
            <Alert color="blue" variant="light" icon={<IconUser size={16} />}>
              Resetting password for <strong>{passwordUser.displayName || passwordUser.username}</strong>
            </Alert>
          )}

          <Group align="flex-end">
            <PasswordInput
              label="New Password"
              placeholder="Enter new password"
              required
              style={{ flex: 1 }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <Button variant="light" onClick={handleGenerateNewPassword}>
              Generate
            </Button>
            {newPassword && (
              <CopyButton value={newPassword}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copied!' : 'Copy password'}>
                    <ActionIcon variant="light" color={copied ? 'green' : 'gray'} onClick={copy}>
                      {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            )}
          </Group>

          <Switch
            label="User must change password at next logon"
            checked={mustChangePassword}
            onChange={(e) => setMustChangePassword(e.currentTarget.checked)}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closePasswordModal(); setPasswordUser(null); setNewPassword(''); }}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              loading={resetPassword.isPending}
              leftSection={<IconKey size={16} />}
            >
              Reset Password
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          closeDeleteModal();
          setDeletingUser(null);
        }}
        title="Delete User"
        size="sm"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
            Are you sure you want to delete <strong>{deletingUser?.displayName || deletingUser?.username}</strong>?
            <Text size="sm" mt="xs">
              This action cannot be undone. The user will be permanently removed from Active Directory.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { closeDeleteModal(); setDeletingUser(null); }}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDeleteUser} loading={deleteUser.isPending}>
              Delete User
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create Group Modal */}
      <Modal
        opened={createGroupModalOpened}
        onClose={() => {
          closeCreateGroupModal();
          resetCreateGroupForm();
        }}
        title="Create New Group"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Group Name"
            placeholder="e.g., Teachers, Students-Grade1"
            required
            leftSection={<IconUsersGroup size={16} />}
            value={createGroupForm.name}
            onChange={(e) => setCreateGroupForm({ ...createGroupForm, name: e.target.value.replace(/\s/g, '-') })}
            description="No spaces allowed - use hyphens instead"
          />

          <Textarea
            label="Description"
            placeholder="Optional description for the group..."
            minRows={2}
            value={createGroupForm.description}
            onChange={(e) => setCreateGroupForm({ ...createGroupForm, description: e.target.value })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeCreateGroupModal(); resetCreateGroupForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              loading={createGroup.isPending}
              leftSection={<IconUsersGroup size={16} />}
            >
              Create Group
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create OU Modal */}
      <Modal
        opened={createOuModalOpened}
        onClose={() => {
          closeCreateOuModal();
          resetCreateOuForm();
        }}
        title="Create Organizational Unit"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="OU Distinguished Name"
            placeholder="e.g., OU=Dijaki,DC=gimid,DC=local"
            required
            leftSection={<IconFolderPlus size={16} />}
            value={createOuForm.dn}
            onChange={(e) => setCreateOuForm({ ...createOuForm, dn: e.target.value })}
            description="Full DN path for the new OU"
          />

          <Textarea
            label="Description"
            placeholder="Optional description for the OU..."
            minRows={2}
            value={createOuForm.description}
            onChange={(e) => setCreateOuForm({ ...createOuForm, description: e.target.value })}
          />

          <Alert color="blue" variant="light" icon={<IconFolderPlus size={16} />}>
            Example OUs:
            <Text size="xs" mt="xs" ff="monospace">OU=Dijaki,DC=gimid,DC=local</Text>
            <Text size="xs" ff="monospace">OU=MEHO,OU=Dijaki,DC=gimid,DC=local</Text>
          </Alert>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { closeCreateOuModal(); resetCreateOuForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateOu}
              loading={createOu.isPending}
              leftSection={<IconFolderPlus size={16} />}
            >
              Create OU
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* User Detail Drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title="User Details"
        position="right"
        size="lg"
      >
        {selectedUserId && (
          <UserDrawerContent userId={selectedUserId} />
        )}
      </Drawer>

      {/* Bulk Add to Group Modal */}
      <BulkGroupModal
        opened={bulkGroupModalOpened}
        onClose={closeBulkGroupModal}
        selectedUserIds={selectedUsers}
        groups={groups}
        onSuccess={() => {
          setSelectedUsers([]);
          closeBulkGroupModal();
        }}
      />
    </Container>
  );
}

// User Drawer Content Component
interface UserDrawerContentProps {
  userId: string;
}

function UserDrawerContent({ userId }: UserDrawerContentProps) {
  const { data: user, isLoading } = useAdUser(userId);
  const { data: groupsData } = useAdGroups();
  const addToGroup = useAddUserToGroup();
  const removeFromGroup = useRemoveUserFromGroup();
  const [selectedGroupToAdd, setSelectedGroupToAdd] = useState<string | null>(null);

  const groups = groupsData?.groups || [];
  const availableGroups = groups.filter((g) => !(user?.groups ?? []).includes(g.name));

  const handleAddToGroup = async () => {
    if (!selectedGroupToAdd || !user) return;
    try {
      await addToGroup.mutateAsync({ userId: user.username, groupId: selectedGroupToAdd });
      notifications.show({
        title: 'Added to Group',
        message: 'User has been added to the group',
        color: 'green',
      });
      setSelectedGroupToAdd(null);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add user to group',
        color: 'red',
      });
    }
  };

  const handleRemoveFromGroup = async (groupName: string) => {
    if (!user) return;
    const group = groups.find((g) => g.name === groupName);
    if (!group) return;

    try {
      await removeFromGroup.mutateAsync({ userId: user.username, groupId: group.name });
      notifications.show({
        title: 'Removed from Group',
        message: `User has been removed from ${groupName}`,
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to remove user from group',
        color: 'red',
      });
    }
  };

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={100} />
        <Skeleton height={200} />
        <Skeleton height={150} />
      </Stack>
    );
  }

  if (!user) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={16} />}>
        User not found
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      {/* User Info Card */}
      <Paper p="md" withBorder radius="md">
        <Group gap="md" mb="md">
          <ThemeIcon size={50} variant="light" color={user.enabled ? 'blue' : 'gray'}>
            <IconUser size={28} />
          </ThemeIcon>
          <div>
            <Text size="lg" fw={600}>
              {user.displayName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username}
            </Text>
            <Text size="sm" c="dimmed" ff="monospace">
              {user.username}
            </Text>
          </div>
        </Group>

        <Group gap={6} mb="md">
          {user.locked && (
            <Badge color="red" leftSection={<IconLock size={10} />}>
              Locked
            </Badge>
          )}
          {!user.enabled && (
            <Badge color="gray">Disabled</Badge>
          )}
          {user.enabled && !user.locked && (
            <Badge color="green">Active</Badge>
          )}
        </Group>

        <Stack gap="xs">
          <Group gap="xs">
            <IconMail size={14} color="gray" />
            <Text size="sm">{user.email || 'No email'}</Text>
          </Group>
          <Group gap="xs">
            <IconId size={14} color="gray" />
            <Text size="sm" c="dimmed">{user.dn}</Text>
          </Group>
          <Group gap="xs">
            <IconCalendar size={14} color="gray" />
            <Text size="sm" c="dimmed">
              Created: {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
            </Text>
          </Group>
          {user.lastLogon && (
            <Group gap="xs">
              <IconShieldCheck size={14} color="gray" />
              <Text size="sm" c="dimmed">
                Last logon: {formatDate(user.lastLogon)}
              </Text>
            </Group>
          )}
        </Stack>

        {user.description && (
          <>
            <Divider my="sm" />
            <Text size="sm" c="dimmed">
              {user.description}
            </Text>
          </>
        )}
      </Paper>

      {/* Groups Section */}
      <Paper p="md" withBorder radius="md">
        <Group justify="space-between" mb="md">
          <Text fw={600}>Groups</Text>
          <Badge variant="light">{user.groups?.length ?? 0}</Badge>
        </Group>

        {(user.groups?.length ?? 0) === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            User is not a member of any groups
          </Text>
        ) : (
          <Stack gap="xs">
            {user.groups!.map((groupName) => (
              <Group key={groupName} justify="space-between" p="xs" style={{ borderRadius: 4 }}>
                <Group gap="xs">
                  <IconUsersGroup size={16} color="gray" />
                  <Text size="sm">{groupName}</Text>
                </Group>
                <Tooltip label="Remove from group">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="red"
                    onClick={() => handleRemoveFromGroup(groupName)}
                    loading={removeFromGroup.isPending}
                  >
                    <IconUserMinus size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            ))}
          </Stack>
        )}

        <Divider my="md" />

        <Group>
          <Select
            placeholder="Add to group..."
            data={availableGroups.filter((g) => g && g.name).map((g) => ({ value: g.name, label: g.name }))}
            value={selectedGroupToAdd}
            onChange={setSelectedGroupToAdd}
            searchable
            style={{ flex: 1 }}
          />
          <Button
            variant="light"
            leftSection={<IconUserPlus size={14} />}
            disabled={!selectedGroupToAdd}
            onClick={handleAddToGroup}
            loading={addToGroup.isPending}
          >
            Add
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}

// Bulk Group Modal Component
interface BulkGroupModalProps {
  opened: boolean;
  onClose: () => void;
  selectedUserIds: string[];
  groups: { name: string }[];
  onSuccess: () => void;
}

function BulkGroupModal({ opened, onClose, selectedUserIds, groups, onSuccess }: BulkGroupModalProps) {
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const addToGroup = useAddUserToGroup();
  const [progress, setProgress] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBulkAdd = async () => {
    if (!selectedGroup || selectedUserIds.length === 0) return;

    setIsProcessing(true);
    setProgress(0);

    try {
      for (const userId of selectedUserIds) {
        await addToGroup.mutateAsync({ userId, groupId: selectedGroup });
        setProgress((prev) => prev + (100 / selectedUserIds.length));
      }

      notifications.show({
        title: 'Users Added to Group',
        message: `${selectedUserIds.length} users have been added to the group`,
        color: 'green',
      });
      onSuccess();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to add users to group',
        color: 'red',
      });
    } finally {
      setIsProcessing(false);
      setProgress(0);
      setSelectedGroup(null);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add Users to Group" size="md">
      <Stack gap="md">
        <Alert color="blue" variant="light">
          Add {selectedUserIds.length} selected user{selectedUserIds.length !== 1 ? 's' : ''} to a group
        </Alert>

        <Select
          label="Select Group"
          placeholder="Choose a group..."
          data={groups.filter((g) => g && g.name).map((g) => ({ value: g.name, label: g.name }))}
          value={selectedGroup}
          onChange={setSelectedGroup}
          searchable
        />

        {isProcessing && (
          <Progress value={progress} animated />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAdd}
            loading={isProcessing}
            disabled={!selectedGroup}
            leftSection={<IconUserPlus size={16} />}
          >
            Add to Group
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default AdUsersPage;
